const getReleaseData = (releaseManagement) => {
  const channelReleased = !!releaseManagement?.channel;

  if (!channelReleased) {
    return {
      isReleased: false,
      releaseDate: null,
    };
  }

  const history = releaseManagement?.history ?? [];
  const latestChannelRelease = [...history]
    .filter((item) => item.type === "Channel" && item.status === "Released")
    .sort((a, b) => new Date(b.actionDate) - new Date(a.actionDate))[0];

  return {
    isReleased: true,
    releaseDate: latestChannelRelease?.actionDate ?? null,
  };
};

export const getLastReleasedObservation = (releaseManagement) => {
  const history = releaseManagement?.history ?? [];

  const lastReleased = [...history]
    .filter((item) => item.type === "Channel" && item.status === "Released")
    .sort((a, b) => new Date(b.actionDate) - new Date(a.actionDate))[0];

  return lastReleased?.observation ?? "";
};

export const mapMaterialAttachmentsToReleasedList = (materials = []) =>
  materials.flatMap((material) =>
    (material.attachments || [])
      .map((attachment) => {
        const { isReleased, releaseDate } = getReleaseData(
          attachment.releaseManagement
        );

        return {
          id: attachment.attachmentId ?? attachment.id,
          fileId: attachment.fileId,
          fileName: attachment.fileName,
          release: isReleased,
          releaseDate,
          version: material.version ?? "",
          comment: getLastReleasedObservation(attachment.releaseManagement),
          chapterNumber: material.episodeNumber,
          blockNumber: null,
          attachmentType: {
            name:
              attachment.type?.name ??
              material.type?.name ??
              "Material Complementar",
          },
          plannedRelease: material?.planning?.release ?? null,
          releasedAt: releaseDate ?? material?.realized?.channelRelease ?? null,
          material,
        };
      })
      .filter((file) => file.release)
  );

export const mapBlockAttachmentsToReleasedList = (blocks = []) =>
  blocks.flatMap((block) =>
    (block.chapters || []).flatMap((chapter) =>
      (chapter.attachments || []).flatMap(
        ({ attachment, olderVersions = [], version }) => {
          const currentAttachment = {
            attachment,
            version,
          };

          const allAttachments = [
            currentAttachment,
            ...olderVersions.map((old) => ({
              attachment: old,
              version: old.version ?? version,
            })),
          ];

          return allAttachments
            .map(({ attachment, version }) => {
              const { isReleased, releaseDate } = getReleaseData(
                attachment?.releaseManagement
              );

              return {
                id: attachment?.attachmentId ?? attachment?.id,
                fileId: attachment?.fileId,
                fileName: attachment?.fileName,
                release: isReleased,
                releaseDate,
                version: version ?? "",
                comment: getLastReleasedObservation(
                  attachment?.releaseManagement
                ),
                chapterNumber: chapter.number,
                blockNumber: block.number,
                attachmentType: {
                  name: attachment?.type?.name ?? "Capítulo/Episódio",
                },
                plannedRelease: block?.planning?.release ?? null,
                releasedAt:
                  releaseDate ?? block?.realized?.channelRelease ?? null,
                chapter,
                block,
              };
            })
            .filter((file) => file.release);
        }
      )
    )
  );

export const normalizeBlockFiles = (files) => {
  if (!Array.isArray(files)) return [];

  const isBlockMode = Array.isArray(files?.[0]?.chapters);

  if (!isBlockMode) return files;

  return [...files]
    .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
    .flatMap((block) =>
      (block.chapters || []).flatMap((chapter) =>
        (chapter.attachments || []).map((att) => {
          const attachment = att.attachment ?? att;

          return {
            ...attachment,
            fileId: attachment.fileId,
            fileName: attachment.fileName,
            blockNumber: block.number,
            chapterNumber: chapter.number,
            type: attachment.type,
            release: attachment.release,
            releaseManagement: attachment.releaseManagement,
            releaseDate: attachment.releaseDate,
          };
        })
      )
    );
};

const isEpisodeMaterial = (material) =>
  material.type?.name === "Capítulo/Episódio" &&
  !!material.episodeId &&
  !!material.episodeNumber;

/**
 * Builds a flat, deduplicated list of addendum attachments out of the
 * planning `blocks` (chapters) and `materials` (episodes) structures.
 *
 * Only addenda released for channel (`releaseManagement.channel === true`)
 * are included. Each addendum is tagged with `isChapter` or `isMaterial`
 * depending on its source. Only the first occurrence of a given attachment
 * id is kept, so an addendum linked to several chapters/episodes is never
 * duplicated.
 */
export const buildAddendaList = ({ blocks = [], materials = [] } = {}) => {
  const addendaMap = new Map();

  const registerAddendum = (addendum, flagKey) => {
    const key = addendum?.attachmentId;

    if (!key || addendaMap.has(key)) return;
    if (!addendum.attachment?.releaseManagement?.channel) return;

    addendaMap.set(key, { ...addendum.attachment, [flagKey]: true });
  };

  for (const block of blocks) {
    for (const chapter of block.chapters ?? []) {
      for (const addendum of chapter.addenda ?? []) {
        registerAddendum(addendum, "isChapter");
      }
    }
  }

  for (const material of materials) {
    if (!isEpisodeMaterial(material)) continue;

    for (const addendum of material.addenda ?? []) {
      registerAddendum(addendum, "isMaterial");
    }
  }

  return Array.from(addendaMap.values());
};

export const getMostRecentDate = (items = [], getDate) => {
  const dates = items
    .map(getDate)
    .filter(Boolean)
    .map((date) => new Date(date).getTime());

  if (!dates.length) return null;

  return new Date(Math.max(...dates));
};

export const buildMaterialGroups = (materials = []) => {
  const grouped = Object.values(
    materials.reduce((acc, material) => {
      const typeId = material.type?.id;

      if (!acc[typeId]) {
        acc[typeId] = {
          typeId,
          typeName: material.type?.name,
          materials: [],
        };
      }

      acc[typeId].materials.push(material);

      return acc;
    }, {})
  );

  return grouped.map((group) => ({
    ...group,
    plannedRelease: getMostRecentDate(
      group.materials,
      (m) => m.planning?.release
    ),
    releasedAt: getMostRecentDate(
      group.materials,
      (m) => m.realized?.channelRelease
    ),
  }));
};

export const buildBlockControllers = (blocks = []) => {
  return [...blocks]
    .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
    .map((block) => ({
      ...block,
      chapters: block.chapters || [],
    }));
};
